/**
 * CadPrimitive — the plot-aware intermediate representation (IR) that sits on
 * the Frontend/Backend boundary of the vector PDF exporter (PDF-1).
 *
 * `Entity → CadPrimitive[]` is the seam: the Frontend (props resolver) reads
 * TYPED `@dxf-ai-editor/drawing-model` entities and emits style-resolved
 * primitives; the Backend (`exportLayoutPdf`) groups them by style and emits
 * batched pdf-lib operators. The IR is intentionally **plot-aware** (carries
 * resolved colour + lineweight, true cubic beziers for arcs) and NOT coupled
 * to the WebGL renderer (`RenderableEntity`, tessellation cache, SDF atlas).
 *
 * # Geometry kinds (PDF-1)
 *   - `path`  — an open or closed sequence of segments (line / cubic-bezier).
 *               Arcs / circles / ellipses are emitted as TRUE cubic beziers so
 *               there is no facet error (`appendBezierCurve` → PDF `c`).
 *   - `text-run` — a recorded text run (position / height / rotation / value).
 *               PDF-1 does NOT render text; it records ATTRIB / TEXT runs so
 *               PDF-2 can render them without re-walking the model.
 *
 * Coordinates are RAW non-offset world (paper-space mm) — the scene adapters
 * bake `applyOffset`, which we deliberately avoid here (the PDF transform owns
 * its own near-origin localization, §4.1).
 *
 * # File layout (CLAUDE.md size control)
 * Per-entity converters live in `./converters/*`. This module owns the IR
 * types and the resolved-style shape; the `Entity → CadPrimitive[]` dispatcher
 * lives in `./entityToPrimitives` (kept separate so the IR type module never
 * imports the converters — breaks the `no-circular` cadPrimitive↔converters
 * cycle).
 */

import type {
  BlockDefinition,
  DimensionStyle,
} from "@dxf-ai-editor/drawing-model";
import type { PlotStyleTable } from "@dxf-ai-editor/acadctb";

import type { LayerColorMap } from "../../scene3d/rendering/colorResolution";
import type { RgbColor } from "./pdfTransform";

/* ── Geometry segments ──────────────────────────────────────────────── */

/** 2D point in RAW world (paper-space mm) coordinates. */
export interface Point2 {
  x: number;
  y: number;
}

/** A straight segment to `to`. */
export interface LineSegment {
  kind: "line";
  to: Point2;
}

/**
 * A cubic Bézier segment with two control points and an end point. Arcs /
 * circles / ellipses are split into ≤90° spans and emitted as exact cubics.
 */
export interface BezierSegment {
  kind: "bezier";
  c1: Point2;
  c2: Point2;
  to: Point2;
}

/** One drawable segment of a path. The path's start point is `Path.start`. */
export type PathSegment = LineSegment | BezierSegment;

/* ── Resolved plot style ────────────────────────────────────────────── */

/**
 * Style resolved for a primitive at emit time. The Backend groups primitives
 * by the tuple (colour, lineweight, linetype) so it can batch one
 * setStrokingRgbColor + setLineWidth per group.
 */
export interface PlotStyle {
  /** Stroke colour (0..1 RGB), already plot-tweaked (white → black). */
  color: RgbColor;
  /** Resolved lineweight in mm (PDF-pt conversion happens in the backend). */
  lineweightMm: number;
  /** Linetype name (Continuous / dashed …). Dashes are PDF-5; kept for grouping. */
  linetype: string;
  /**
   * Per-entity linetype scale (PDF-6.1 — DXF group 48 / CELTSCALE-per-entity).
   * Multiplies the dash-pattern length for this entity on top of the
   * drawing-level $LTSCALE. Defaults to 1 (undefined treated as 1). Folded
   * into {@link plotStyleKey} so two otherwise-identical styles with a
   * different scale batch into separate dash runs.
   */
  linetypeScale?: number;
  /**
   * Per-entity stroke + fill alpha in `(0, 1]` (PDF-6.3b — CTB/STB screening).
   * `undefined` (or 1) is fully opaque and the emitter omits the ExtGState
   * wrapper (byte-identical to PDF-1..6.3a). When present, the emitter wraps
   * the primitive's paint operators in `q /GSaN gs … Q` where `/GSaN` is the
   * page-Resources ExtGState carrying `/CA` (stroke) + `/ca` (fill) = alpha.
   *
   * Folded into {@link plotStyleKey} (rounded to 3 decimals) so primitives with
   * distinct alphas batch into separate paint groups; otherwise an opaque and
   * a translucent primitive sharing colour/lw/linetype would coalesce and the
   * translucent one's alpha would silently apply to the opaque one too.
   */
  alpha?: number;
  /**
   * "Do not plot" sentinel (PDF-6.3b — CTB `plot=false` / `screen <= 0`). When
   * `true` the converter MUST short-circuit to an empty primitive list; the
   * sheet emitter additionally guards in {@link emitLayeredPaths} /
   * {@link emitLayeredText} as defence in depth (mirroring how `omitLayers`
   * (PDF-6.9b) filters omit-listed layers).
   *
   * Carried on the style rather than on the primitive so converter authors only
   * have to honour ONE return contract (`if (style.skip) return [];`) regardless
   * of which sub-primitives the entity would have produced.
   */
  skip?: boolean;
}

/** Stable grouping key for a {@link PlotStyle}. */
export function plotStyleKey(style: PlotStyle): string {
  const c = style.color;
  const lts = style.linetypeScale ?? 1;
  // PDF-6.3b: fold alpha into the run key so translucent vs opaque primitives
  // never share one batch (they need different ExtGState wrappers). Rounded to
  // 3 decimals to match the ExtGState resource dedup ({@link ensureAlphaExtGState}).
  const alpha = style.alpha ?? 1;
  const aKey = Math.round(alpha * 1000) / 1000;
  return `${c.r.toFixed(4)},${c.g.toFixed(4)},${c.b.toFixed(4)}|${style.lineweightMm.toFixed(4)}|${style.linetype}|${lts.toFixed(4)}|${aKey.toFixed(3)}`;
}

/* ── Primitives ─────────────────────────────────────────────────────── */

/** One closed loop of a multi-subpath path (PDF-5a hatch holes). */
export interface PathSubpath {
  start: Point2;
  segments: PathSegment[];
}

/** A stroked (and optionally filled) path primitive. */
export interface PathPrimitive {
  type: "path";
  /** Owning entity id (debug / draw-order). */
  entityId: string;
  /**
   * Resolved OCG/AutoCAD layer name the primitive files under (PDF-6.2a). This
   * is the DRAWING layer used for layer-aware ordered segmentation
   * ({@link layerSections}) and, in PDF-6.2b, the BDC/EMC marked-content layer
   * (`/OCProperties`). A block child on layer "0" inherits the INSERT's layer;
   * a child on any other layer keeps its own (AutoCAD rule — matches
   * `state/blockReducerHelpers.ts resolveChildLayer`). 6.2a only records it; it
   * does NOT alter the emitted operator stream (output stays byte-identical).
   */
  layer: string;
  start: Point2;
  segments: PathSegment[];
  /** Closed path (emits `h` before stroke). */
  closed: boolean;
  /**
   * Fill rule for closed loops (PDF-1 emits nothing filled by default — stroke
   * only — but the IR carries the rule so PDF-5 hatch can reuse it):
   * `none` = stroke only; `nonzero` = `f`; `evenodd` = `f*`.
   */
  fill: "none" | "nonzero" | "evenodd";
  /**
   * Whether the path is stroked (PDF-5a). Defaults to `true` (PDF-1 behaviour).
   * A SOLID hatch sets this `false` + `fill !== "none"` so the backend emits a
   * FILL-ONLY paint op (`f` / `f*`) instead of fill-and-stroke (`B` / `B*`):
   * AutoCAD solid hatches fill their region without stroking the boundary
   * (the boundary polyline, if drawn, is a separate entity).
   */
  stroke?: boolean;
  /**
   * Additional closed loops painted in the SAME paint op as `start`/`segments`
   * (PDF-5a multi-loop hatch). When present, the backend emits one
   * `moveTo … closePath` block per subpath (including the implicit first loop
   * carried by `start`/`segments`) and then a SINGLE fill op — so an even-odd
   * fill rule produces the hole between an outer loop and an inner loop.
   * Undefined / empty for single-loop primitives (PDF-1 behaviour unchanged).
   */
  subpaths?: PathSubpath[];
  style: PlotStyle;
}

/**
 * A pattern-fill hatch (PDF-5a P2). Unlike a stroked/filled {@link PathPrimitive},
 * this carries the boundary loops as a CLIP region plus the resolved pattern
 * line families; the backend emits a self-contained `q · (loops) W* n · (pattern
 * strokes clipped to the region) · Q` block. The clip approach lets PDF clip the
 * infinite pattern lines to the boundary, avoiding manual line/boundary
 * intersection (mirrors the VP clip in `sheetRenderer`).
 */
export interface HatchPatternPrimitive {
  type: "hatch-pattern";
  entityId: string;
  /**
   * Resolved OCG/AutoCAD layer name (PDF-6.2a — see {@link PathPrimitive.layer}).
   * The drawing layer this hatch files under for layer-aware ordered
   * segmentation; recorded only, output unchanged.
   */
  layer: string;
  /** Closed boundary loops (RAW world mm) forming the even-odd clip region. */
  loops: PathSubpath[];
  /** Resolved pattern line segments (RAW world mm), pre-clipped to the bbox. */
  lines: HatchPatternLine[];
  /** Stroke colour for the pattern lines (the hatch plot colour). */
  color: RgbColor;
  /** Dash pattern (mm) per line family, applied to its lines. Empty = solid. */
  style: PlotStyle;
}

/** One straight pattern line segment in RAW world mm (already laid across bbox). */
export interface HatchPatternLine {
  from: Point2;
  to: Point2;
  /** Dash array in world mm (positive = dash, |negative| = gap). Empty = solid. */
  dashes: number[];
}

/**
 * Resolved two-endpoint colour ramp for a {@link GradientHatchPrimitive}
 * (PDF-6.5b). `color0`/`color1` are the FINAL plot RGB (0..1) for the two ends
 * of the ramp — the converter has already folded oneColor/tint and the INV*
 * swap into these two endpoints, so the shading builder only needs the two
 * colours plus the flags below.
 */
export interface GradientHatchSpec {
  /** Predefined gradient name (DXF group 470): LINEAR / CYLINDER / … . */
  name: string;
  /**
   * The PDF ShadingType family this gradient maps to:
   *   - `"axial"`  → PDF ShadingType 2 (LINEAR / CYLINDER / INVCYLINDER /
   *     CURVED / INVCURVED);
   *   - `"radial"` → PDF ShadingType 3 (SPHERICAL / INVSPHERICAL /
   *     HEMISPHERICAL / INVHEMISPHERICAL).
   */
  shadingType: "axial" | "radial";
  /**
   * INV* name → the colour order is swapped (c0↔c1). Already applied to
   * `color0`/`color1` by the converter, so the shading builder need NOT swap
   * again — the flag is carried for the function builder's stitching encode and
   * for test introspection.
   */
  inverse: boolean;
  /**
   * CYLINDER / INVCYLINDER → a SYMMETRIC ramp c0→c1→c0 (the highlight runs
   * down the middle of the axis). Emitted as a FunctionType 3 stitching of two
   * Type-2 subfunctions.
   */
  symmetric: boolean;
  /** Gradient rotation in RADIANS (DXF group 460). The axis/disc orientation. */
  rotation: number;
  /** Gradient shift in `[0, 1]` (DXF group 461). 0 = centred. */
  shift: number;
  /** Single-colour tint mode (DXF group 452 = 1). Recorded for diagnostics. */
  oneColor: boolean;
  /** Tint value in `[0, 1]` (DXF group 462). Recorded for diagnostics. */
  tint: number;
  /** Resolved plot RGB for ramp end 0 (after INV swap + tint fold). */
  color0: RgbColor;
  /** Resolved plot RGB for ramp end 1 (after INV swap + tint fold). */
  color1: RgbColor;
}

/**
 * A gradient-fill hatch (PDF-6.5b). AutoCAD gradient hatches carry an
 * axial/radial colour ramp (DXF groups 450–470, modelled on
 * `HatchEntity.gradient` since PDF-6.5a). Unlike the solid fallback the prior
 * slice emitted, this primitive renders a REAL PDF shading (ShadingType 2 axial
 * / 3 radial — PDF 32000-1:2008 §8.7.4.5) clipped to the hatch boundary.
 *
 * The backend emits a self-contained `q · (loops) W* n · sh · Q` block: the
 * boundary loops form an even-odd clip, then a single `sh` operator paints the
 * registered shading across the clipped region (`/Extend [true true]` covers the
 * whole region). The axis/disc geometry is computed at EMIT time from `bbox`
 * (world mm) + `gradient.rotation`/`shift`, transformed to pt so VP rotation /
 * scale carry correctly (mirrors {@link HatchPatternPrimitive}).
 */
export interface GradientHatchPrimitive {
  type: "gradient-hatch";
  entityId: string;
  /**
   * Resolved OCG/AutoCAD layer name (PDF-6.2a — see {@link PathPrimitive.layer}).
   * The drawing layer this hatch files under for layer-aware ordered
   * segmentation; recorded for the marked-content + omit-layer gates.
   */
  layer: string;
  /** Closed boundary loops (RAW world mm) forming the even-odd clip region. */
  loops: PathSubpath[];
  /**
   * Boundary bounding box (RAW world mm). The gradient axis / disc geometry is
   * derived from this at emit time (then transformed to pt). Carried on the
   * primitive so the emitter need not re-walk the loops (it can still recompute
   * defensively if absent).
   */
  bbox: { minX: number; minY: number; maxX: number; maxY: number };
  /** The resolved gradient ramp + flags (see {@link GradientHatchSpec}). */
  gradient: GradientHatchSpec;
  /** Resolved plot style (alpha / merge / skip honoured by the emitter). */
  style: PlotStyle;
}

/**
 * A recorded text run. PDF-1 does NOT render text — this primitive captures
 * everything PDF-2 needs (value / position / height / rotation / style /
 * justification) so ATTRIB stamp text is never lost.
 */
export interface TextRunPrimitive {
  type: "text-run";
  entityId: string;
  /**
   * Resolved OCG/AutoCAD layer name (PDF-6.2a — see {@link PathPrimitive.layer}).
   * The drawing layer this text run files under for layer-aware ordered
   * segmentation; recorded only, output unchanged. A text run from an INSERT
   * ATTRIB / block-child TEXT files under the INSERT's resolved layer (the
   * attrib carries no independent layer in the model), matching the scene.
   */
  layer: string;
  /** Origin / insertion point in RAW world mm. */
  position: Point2;
  /** Text height in world mm. */
  height: number;
  /** Rotation in degrees (CCW, AutoCAD convention). */
  rotation: number;
  /** Width factor (DXF code 41). */
  widthFactor: number;
  /** Oblique angle in degrees (DXF code 51). */
  obliqueAngle: number;
  /** Horizontal justification (DXF code 72). */
  hJustification: number;
  /** Vertical justification (DXF code 74). */
  vJustification: number;
  /** Text-style name. */
  styleName: string;
  /** The string value to render. */
  value: string;
  /** Plot colour (text inherits entity colour). */
  color: RgbColor;
  /**
   * PDF-6.3b — CTB/STB screening alpha for the text glyphs. Same semantics as
   * {@link PlotStyle.alpha}: undefined / 1 is opaque (no ExtGState wrapper),
   * a value in (0, 1) triggers the `q /GSaN gs … Q` save-state block around
   * the painted glyphs.
   */
  alpha?: number;
  /**
   * PDF-6.3b — do-not-plot sentinel. The text converter sets this when the
   * CTB/STB entry says `plot=false` (or `screen <= 0`); the sheet emitter
   * filters it out before painting (defence-in-depth, mirrors the path gate
   * in {@link emitLayeredPaths}).
   */
  skip?: boolean;
  /**
   * GitHub Codex review PR #558 round 2 P2 — MIRRTEXT glyph-handedness: a
   * VERTICALLY-reflected (upside-down/local-Y-flip) glyph run, mirroring
   * the WebGL renderer's `mirrored` scene primitive field
   * (`sceneTextMeshFactories.ts::applyMirrorScale`'s `mesh.scale.set(1,
   * -1, 1)` for the "mtext" kind — round-1 local read-only follow-up
   * corrected this from an initial wrong local-X assumption).
   * `dimensionConverter.ts`/`multileaderConverter.ts` set this from
   * `DimensionPrimitive`/MLeader `mirrored` so a MIRRTEXT=1
   * dimension/multileader's PDF glyphs match the viewport/DXF instead of
   * painting normal (unreflected) text at the correctly-reflected ANGLE.
   * `insertAttributes.ts` sets this from a block-ATTRIB's `generationFlags`
   * bit 4 (`TEXT_GEN_FLAG_UPSIDE_DOWN`). Undefined/false is the common
   * (unmirrored) case — no behaviour change for every existing text run.
   */
  mirrored?: boolean;
  /**
   * GitHub Codex review PR #558 round 2 (round-9 GitHub bot follow-up) P2
   * — the INDEPENDENT local-X-flip companion to {@link mirrored}'s local-Y
   * flip, mirroring the WebGL "text"-kind renderer's SEPARATE `backward`
   * bit (`sceneTextMeshFactories.ts::applyMirrorScale`'s `sx = g.backward
   * ? -1 : 1` — `textGeometry.ts`'s own 2-bit `generationFlags` decode:
   * bit 2 = backward/local-X, bit 4 = upsideDown/local-Y, independently
   * settable). ONLY a block-ATTRIB (which renders through the SAME
   * standalone-TEXT 2-bit path, unlike Dimension/Multileader's Y-only
   * `mirrored` convention) ever sets this — `insertAttributes.ts` decodes
   * it from `generationFlags` bit 2. Dimension/Multileader never carry a
   * `backward`-equivalent bit, so their converters never set this field
   * (stays undefined/false — no behaviour change for those runs).
   */
  backward?: boolean;
}

/** The CadPrimitive IR union. */
export type CadPrimitive =
  | PathPrimitive
  | TextRunPrimitive
  | HatchPatternPrimitive
  | GradientHatchPrimitive;

/* ── Conversion context ─────────────────────────────────────────────── */

/**
 * Inputs every converter needs: the layer colour table, the block-definition
 * map (for INSERT recursion), the parent INSERT colour (BYBLOCK chain) and a
 * paper-space tessellation tolerance (in mm) controlling the maximum chord
 * error when an arc/ellipse must fall back to line segments. Arcs are emitted
 * as TRUE beziers so the tolerance mostly governs degenerate fallbacks.
 */
export interface CadConvertContext {
  layerColors: LayerColorMap;
  /**
   * Layer lineweights keyed by LOWER-CASED layer name → the layer record's
   * raw lineweight value (a DXF code/number: −3 default, −1 bylayer, 0
   * hairline, or 0.01-mm units). A ByLayer entity (lw −1) resolves its plotted
   * pen width through this map; when a layer is absent the decoder falls back
   * to Default (0.25 mm). Optional so legacy contexts (and tests that only
   * exercise colour) need not supply it.
   */
  layerLineweights?: ReadonlyMap<string, number>;
  /**
   * Layer linetypes keyed by LOWER-CASED layer name → the layer record's
   * assigned linetype name (PDF-5c). A ByLayer entity resolves its plotted
   * dash pattern through this map (the layer's linetype); when a layer is
   * absent the resolver falls back to "Continuous" (solid). Optional so legacy
   * contexts (and tests that only exercise geometry / colour) need not supply
   * it — an absent map yields Continuous for every ByLayer entity.
   */
  layerLinetypes?: ReadonlyMap<string, string>;
  blocks: Record<string, BlockDefinition>;
  /**
   * Named DIMENSION styles from the document's `styleTables.dimensionStyles`
   * (PDF-5b). The DIMENSION converter feeds them to the scene adapter so a
   * dimension's referenced style resolves its DIMVARs (DIMTXT / DIMASZ / DIMBLK
   * …) instead of always falling back to defaults. Optional so legacy contexts
   * (and tests that only exercise geometry) need not supply it — an absent /
   * empty table yields default DIMVARs (AutoCAD's "Standard" defaults).
   */
  dimensionStyles?: readonly DimensionStyle[];
  /** Resolved colour of the enclosing INSERT (BYBLOCK), or null at root. */
  blockColor: string | null;
  /**
   * PDF-6.3b — the enclosing INSERT's AutoCAD Color Index (ACI 1..256) for a
   * BYBLOCK child's CTB lookup, or null when it cannot be determined (a ByBlock
   * INSERT at root, a ByLayer INSERT whose layer colour is not a literal ACI,
   * etc.). A CTB is positional by ACI, but a BYBLOCK child's OWN colour has no
   * index — its plot style must follow the enclosing INSERT's ACI. `blockColor`
   * already collapsed that ACI to a hex, so the index is carried separately
   * here for the do-not-plot / screening lookup. Undefined at root (the plain
   * per-entity converters do not set it) → treated as null.
   */
  blockAci?: number | null;
  /** Resolved lineweight (mm) of the enclosing INSERT, or undefined at root. */
  blockLineweightMm?: number;
  /**
   * Resolved linetype name of the enclosing INSERT (BYBLOCK chain, PDF-5c), or
   * null at root. A ByBlock entity's dash pattern inherits this. Undefined /
   * null → Continuous.
   */
  blockLinetype?: string | null;
  /**
   * Max chord error in paper-space mm for bezier-span / fallback segment
   * counts. ~0.1 mm ≈ plotter dot at 300 dpi. NOT camera-zoom dependent.
   */
  toleranceMm: number;
  /**
   * PDF-6.3b — the resolved CTB / STB plot-style table the user loaded in the
   * PDF Options sub-dialog (or threaded via `ExportLayoutPdfOptions`).
   *
   * The per-entity style resolver ({@link resolvePlotStyle}) looks up the
   * matching {@link PlotStyleEntry} by ACI (CTB) or named-style key (STB) and
   * applies the four override paths: colour, lineweight, linetype, alpha (from
   * screening). When `entry.plot === false` (or `screen <= 0`) the resolver
   * returns a `skip` style and the converter short-circuits to an empty list.
   *
   * Optional: an absent table preserves the pre-6.3b behaviour byte-for-byte
   * (no override, no skip), so existing converter tests stay unchanged.
   */
  plotStyleTable?: PlotStyleTable;
  /**
   * PDF-6.3b — the LAYOUT's `plotSettings.plotStyleName` (when an STB is the
   * loaded table) used as a COARSE per-entity style key. The typed model does
   * NOT currently carry a per-entity `plotStyleName`; the layer table does
   * (PR-LT1 importer) but the resolver consults the layout-level name as a
   * fallback when no layer-level name applies. Empty / undefined → "Normal".
   *
   * For a CTB this field is unused (the lookup is positional by ACI).
   */
  layoutPlotStyleName?: string;
  /**
   * PDF-6.4 — AutoCAD "Lines merge / Overwrite" plot setting (PC3 parity).
   *
   *   - `"overwrite"` (the default and pre-6.4 behaviour): top objects hide
   *     bottom — PDF's `Normal` blend mode. The exporter does NOT emit any
   *     `/BM` ExtGState, so output is byte-identical to PDF-6.3b for the
   *     legacy default.
   *   - `"merge"`: overlapping geometry darkens — the AutoCAD "Lines merge"
   *     setting. The exporter wraps EVERY painted primitive in an ExtGState
   *     carrying `/BM /Multiply` so PDF compositing multiplies overlapping
   *     colours (matches AutoCAD's plotted appearance).
   *
   * Threaded session-level rather than per-LAYOUT-persist: the `PlotLayoutFlags`
   * bitfield has 9 flags and no Merge-Control bit, so a per-layout persist
   * would have no DXF home. AutoCAD's PC3 file is also a session / output-device
   * choice, not a per-layout setting.
   *
   * Absent / undefined collapses to `"overwrite"` (default behaviour, no /BM
   * emission).
   */
  mergeControl?: "overwrite" | "merge";
  /**
   * The drawing's `$FILLMODE` HEADER variable (`DrawingDocument.fillmode`),
   * threaded from `sheetRenderer.ts` so the plot paints a wide polyline's band
   * in the state the DOCUMENT is in rather than in an assumed one.
   *
   * Autodesk system-variable reference, FILLMODE: "Specifies whether hatches
   * and fills, 2D solids, and wide polylines are filled in" — type Integer,
   * INITIAL VALUE 1, saved in the DRAWING. 1 fills; 0 draws the same objects as
   * an OUTLINE only — Autodesk's FILL (Command), Off: "Only the outlines of
   * objects are displayed and plotted."
   *
   * OPTIONAL, and absent MEANS 1, mirroring both the HEADER rule (`ezdxf`
   * `sections/headervars.py`: `$FILLMODE`, code 70, `default=1`) and the
   * renderer's own projection (`scene3d` — `RenderableEntity.fillmode` is
   * stamped only for the non-default 0).
   *
   * Two readers, both through the same `fillModePaintsSolid` predicate the
   * canvas uses, so screen and paper cannot hold two opinions about the
   * variable:
   *   - `converters/widePolylineBand.ts::bandPathPrimitive` — the band is
   *     fill-only at 1, stroke-only over the same rings at 0 (@codex PR #785
   *     round 12, F1);
   *   - `converters/hatchConverter.ts::hatchToPrimitives` — a SOLID, GRADIENT
   *     or PATTERNED hatch plots as its stroked boundary at 0 (@codex PR #785
   *     round 16, P2).
   */
  fillmode?: number;
}

/** Default paper-space tessellation tolerance (mm). */
export const DEFAULT_PAPER_TOLERANCE_MM = 0.1;

export type { LayerColorMap };
